// ============================================================
// DEV-194: Tenant Subscription and Billing
// ============================================================

import { TenantType } from './tenant-base.model';
import { ResourceType, UsageSummary } from './tenant-quota.model';

/**
 * Subscription plan types
 */
export type SubscriptionPlan = 'enterprise' | 'professional' | 'standard' | 'trial' | 'free';

/**
 * Billing cycle
 */
export type BillingCycle = 'monthly' | 'quarterly' | 'annual';

/**
 * Payment status
 */
export type PaymentStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'refunded'
  | 'partially_refunded'
  | 'cancelled';

/**
 * Invoice status
 */
export type InvoiceStatus =
  'draft' | 'pending' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'refunded';

/**
 * Subscription status
 */
export type SubscriptionStatus =
  'active' | 'trialing' | 'past_due' | 'cancelled' | 'paused' | 'expired';

/**
 * Subscription plan features
 */
export interface PlanFeatures {
  maxUsers: number;
  maxStrategies: number;
  maxOrders: number;
  maxTrades: number;
  storageGb: number;
  apiCallsPerMonth: number;
  supportLevel: 'community' | 'email' | 'priority' | 'dedicated';
  dataRetentionDays: number;
  backtesting: boolean;
  mlFeatures: boolean;
  advancedAnalytics: boolean;
  customIntegrations: boolean;
  whiteLabel: boolean;
  sla: boolean;
  dedicatedInfra: boolean;
}

/**
 * Subscription plan definition
 */
export interface SubscriptionPlanDefinition {
  id: SubscriptionPlan;
  name: string;
  description: string;
  monthlyPrice: number;
  annualPrice: number;
  currency: string;
  features: PlanFeatures;
  popular?: boolean;
  enterprise?: boolean;
}

/**
 * Tenant subscription
 */
export interface TenantSubscription {
  id: string;
  tenantId: string;
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  billingCycle: BillingCycle;
  startDate: string;
  endDate?: string;
  trialEndDate?: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  cancelledAt?: string;
  cancelReason?: string;
  pausedAt?: string;
  resumeAt?: string;
  quantity: number; // Number of seats/units
  discount?: SubscriptionDiscount;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

/**
 * Subscription discount
 */
export interface SubscriptionDiscount {
  id: string;
  code?: string;
  type: 'percentage' | 'fixed';
  value: number;
  startDate: string;
  endDate?: string;
  appliedAt: string;
}

/**
 * Invoice line item
 */
export interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  taxAmount: number;
  taxRate: number;
  periodStart?: string;
  periodEnd?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Invoice
 */
export interface Invoice {
  id: string;
  number: string;
  tenantId: string;
  subscriptionId?: string;
  status: InvoiceStatus;
  currency: string;
  subtotal: number;
  taxAmount: number;
  discountAmount: number;
  total: number;
  amountDue: number;
  amountPaid: number;
  lineItems: InvoiceLineItem[];
  dueDate: string;
  paidAt?: string;
  periodStart: string;
  periodEnd: string;
  billingAddress?: BillingAddress;
  notes?: string;
  pdfUrl?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Billing address
 */
export interface BillingAddress {
  line1: string;
  line2?: string;
  city: string;
  state?: string;
  postalCode: string;
  country: string;
}

/**
 * Payment method
 */
export interface PaymentMethod {
  id: string;
  tenantId: string;
  type: 'card' | 'bank_transfer' | 'paypal' | 'invoice';
  isDefault: boolean;
  card?: {
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
    holderName?: string;
  };
  bankAccount?: {
    bankName: string;
    accountType: 'checking' | 'savings';
    last4: string;
    holderName: string;
  };
  billingAddress?: BillingAddress;
  createdAt: string;
  updatedAt: string;
}

/**
 * Payment
 */
export interface Payment {
  id: string;
  tenantId: string;
  invoiceId?: string;
  subscriptionId?: string;
  paymentMethodId?: string;
  status: PaymentStatus;
  amount: number;
  currency: string;
  description?: string;
  failureReason?: string;
  refundedAmount?: number;
  metadata?: Record<string, unknown>;
  processedAt?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Billing summary
 */
export interface BillingSummary {
  tenantId: string;
  currentBalance: number;
  pendingCharges: number;
  nextBillingDate?: string;
  currency: string;
  status: string;
}

/**
 * Usage-based billing item
 */
export interface UsageBasedCharge {
  resourceType: ResourceType;
  quantity: number;
  unitPrice: number;
  amount: number;
  includedInPlan: number;
  overageQuantity: number;
  overageAmount: number;
}

/**
 * Subscription plan info lookup
 */
export const SUBSCRIPTION_PLAN_INFO: Record<SubscriptionPlan, SubscriptionPlanDefinition> = {
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Full-featured plan with dedicated infrastructure and premium support',
    monthlyPrice: 999,
    annualPrice: 9990, // 2 months free
    currency: 'USD',
    enterprise: true,
    features: {
      maxUsers: -1, // Unlimited
      maxStrategies: -1,
      maxOrders: -1,
      maxTrades: -1,
      storageGb: 1000,
      apiCallsPerMonth: -1,
      supportLevel: 'dedicated',
      dataRetentionDays: 3650,
      backtesting: true,
      mlFeatures: true,
      advancedAnalytics: true,
      customIntegrations: true,
      whiteLabel: true,
      sla: true,
      dedicatedInfra: true
    }
  },
  professional: {
    id: 'professional',
    name: 'Professional',
    description: 'Advanced features for growing trading teams',
    monthlyPrice: 299,
    annualPrice: 2990,
    currency: 'USD',
    popular: true,
    features: {
      maxUsers: 50,
      maxStrategies: 100,
      maxOrders: 100000,
      maxTrades: 500000,
      storageGb: 100,
      apiCallsPerMonth: 1000000,
      supportLevel: 'priority',
      dataRetentionDays: 365,
      backtesting: true,
      mlFeatures: true,
      advancedAnalytics: true,
      customIntegrations: true,
      whiteLabel: false,
      sla: true,
      dedicatedInfra: false
    }
  },
  standard: {
    id: 'standard',
    name: 'Standard',
    description: 'Essential features for individual traders',
    monthlyPrice: 99,
    annualPrice: 990,
    currency: 'USD',
    features: {
      maxUsers: 10,
      maxStrategies: 25,
      maxOrders: 10000,
      maxTrades: 50000,
      storageGb: 25,
      apiCallsPerMonth: 100000,
      supportLevel: 'email',
      dataRetentionDays: 180,
      backtesting: true,
      mlFeatures: false,
      advancedAnalytics: false,
      customIntegrations: false,
      whiteLabel: false,
      sla: false,
      dedicatedInfra: false
    }
  },
  trial: {
    id: 'trial',
    name: 'Trial',
    description: '14-day free trial with full Professional features',
    monthlyPrice: 0,
    annualPrice: 0,
    currency: 'USD',
    features: {
      maxUsers: 5,
      maxStrategies: 10,
      maxOrders: 1000,
      maxTrades: 5000,
      storageGb: 5,
      apiCallsPerMonth: 10000,
      supportLevel: 'email',
      dataRetentionDays: 14,
      backtesting: true,
      mlFeatures: true,
      advancedAnalytics: true,
      customIntegrations: false,
      whiteLabel: false,
      sla: false,
      dedicatedInfra: false
    }
  },
  free: {
    id: 'free',
    name: 'Free',
    description: 'Basic access with limited features',
    monthlyPrice: 0,
    annualPrice: 0,
    currency: 'USD',
    features: {
      maxUsers: 1,
      maxStrategies: 3,
      maxOrders: 100,
      maxTrades: 500,
      storageGb: 1,
      apiCallsPerMonth: 1000,
      supportLevel: 'community',
      dataRetentionDays: 30,
      backtesting: false,
      mlFeatures: false,
      advancedAnalytics: false,
      customIntegrations: false,
      whiteLabel: false,
      sla: false,
      dedicatedInfra: false
    }
  }
};

/**
 * Get plan info
 */
export function getPlanInfo(plan: SubscriptionPlan): SubscriptionPlanDefinition {
  return SUBSCRIPTION_PLAN_INFO[plan];
}

/**
 * Get price for plan and billing cycle
 */
export function getPlanPrice(plan: SubscriptionPlan, cycle: BillingCycle): number | null {
  const info = SUBSCRIPTION_PLAN_INFO[plan];
  if (cycle === 'annual') {
    return info.annualPrice;
  }
  if (cycle === 'quarterly') {
    // No authoritative quarterly pricing/discount contract exists.
    return null;
  }
  return info.monthlyPrice;
}

/**
 * Check if can upgrade from one plan to another
 */
export function canUpgradePlan(from: SubscriptionPlan, to: SubscriptionPlan): boolean {
  const planOrder: SubscriptionPlan[] = ['free', 'trial', 'standard', 'professional', 'enterprise'];
  return planOrder.indexOf(to) > planOrder.indexOf(from);
}

/**
 * Check if can downgrade from one plan to another
 */
export function canDowngradePlan(from: SubscriptionPlan, to: SubscriptionPlan): boolean {
  const planOrder: SubscriptionPlan[] = ['free', 'trial', 'standard', 'professional', 'enterprise'];
  return planOrder.indexOf(to) < planOrder.indexOf(from) && to !== 'trial';
}

/**
 * Format currency amount.
 *
 * Returns an em-dash sentinel ("—") for non-finite amounts rather than
 * leaking "$NaN" into the UI. Returns the sentinel for invalid currency
 * codes too — Intl.NumberFormat throws RangeError on unsupported codes and a
 * helper used across billing templates must not surface that to end users.
 * Negative values are legitimate (refunds, overage credits) and pass through.
 * Note: this function is a pure formatter and does not log; callers that need
 * to telemeter anomalies should do so at the call site with their own logger.
 */
export function formatCurrency(amount: number, currency: string = 'USD'): string {
  if (!Number.isFinite(amount)) {
    return '—';
  }
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(amount);
  } catch {
    // Invalid currency code (e.g. 'XXX-FAKE') — fall back to the sentinel.
    return '—';
  }
}

/**
 * Create default subscription for tenant type.
 *
 * @throws {Error} when `tenantId` is empty or whitespace-only — a subscription
 *   without a tenant identifier is meaningless and can silently create ghost
 *   billing records if callers bypass the type system (e.g. via an
 *   `as string` cast). The error message is stable (code prefix
 *   `ERR_TENANT_ID_REQUIRED`) so UI layers can map it to user-safe copy
 *   without string-matching on the internal helper name.
 */
export function createDefaultSubscription(
  tenantId: string,
  tenantType: TenantType
): Partial<TenantSubscription> {
  if (!tenantId?.trim()) {
    throw new Error('ERR_TENANT_ID_REQUIRED: a non-empty tenant identifier is required');
  }

  const now = new Date();
  const trialEnd = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000); // 14 days

  const planMapping: Record<TenantType, SubscriptionPlan> = {
    enterprise: 'enterprise',
    professional: 'professional',
    standard: 'standard',
    trial: 'trial',
    internal: 'enterprise'
  };

  return {
    tenantId,
    plan: planMapping[tenantType] ?? 'trial',
    status: tenantType === 'trial' ? 'trialing' : 'active',
    billingCycle: 'monthly',
    startDate: now.toISOString(),
    trialEndDate: tenantType === 'trial' ? trialEnd.toISOString() : undefined,
    currentPeriodStart: now.toISOString(),
    currentPeriodEnd: new Date(now.getFullYear(), now.getMonth() + 1, now.getDate()).toISOString(),
    cancelAtPeriodEnd: false,
    quantity: 1,
    createdAt: now.toISOString(),
    updatedAt: now.toISOString()
  };
}
